import { Injectable, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { firstValueFrom } from 'rxjs';

const TOKEN_KEY = 'portal_access_token';
const USER_KEY = 'portal_user';

export interface Client {
  id: number;
  name: string;
  code: number;
}

export interface PortalUser {
  id: string;
  name: string;
  email: string;
  rol: string;
  isActive: boolean;
  client: Client;
}

export interface AuthResponse {
  user: PortalUser;
  accessToken: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly router = inject(Router);
  private readonly http = inject(HttpClient);
  private readonly apiBaseUrl = environment.apiBaseUrl || 'http://localhost:3000/api/';
  private readonly _isAuthenticated = signal<boolean>(this.hasToken());
  private readonly _currentUser = signal<PortalUser | null>(this.loadUser());

  isAuthenticated = this._isAuthenticated.asReadonly();
  currentUser = this._currentUser.asReadonly();

  private hasToken(): boolean {
    return !!localStorage.getItem(TOKEN_KEY);
  }

  private loadUser(): PortalUser | null {
    const userJson = localStorage.getItem(USER_KEY);
    if (!userJson) return null;
    try {
      return JSON.parse(userJson);
    } catch {
      return null;
    }
  }

  getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  }

  setToken(token: string): void {
    localStorage.setItem(TOKEN_KEY, token);
    this._isAuthenticated.set(true);
  }

  setUser(user: PortalUser): void {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    this._currentUser.set(user);
  }

  setAuthData(token: string, user: PortalUser): void {
    this.setToken(token);
    this.setUser(user);
  }

  logout(): void {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    this._isAuthenticated.set(false);
    this._currentUser.set(null);
    this.router.navigate(['/login']);
  }

  checkAuth(): void {
    this._isAuthenticated.set(this.hasToken());
    this._currentUser.set(this.loadUser());
  }

  // API methods
  activate(token: string, password: string): Promise<AuthResponse> {
    return firstValueFrom(
      this.http.post<AuthResponse>(`${this.apiBaseUrl}portal-auth/activate`, {
        token,
        password,
      })
    );
  }

  login(email: string, password: string): Promise<AuthResponse> {
    return firstValueFrom(
      this.http.post<AuthResponse>(`${this.apiBaseUrl}portal-auth/login`, {
        email,
        password,
      })
    );
  }

  forgotPassword(email: string): Promise<{ message: string }> {
    return firstValueFrom(
      this.http.post<{ message: string }>(`${this.apiBaseUrl}portal-auth/forgot-password`, {
        email,
      })
    );
  }

  resetPassword(token: string, password: string): Promise<{ message: string }> {
    return firstValueFrom(
      this.http.post<{ message: string }>(`${this.apiBaseUrl}portal-auth/reset-password`, {
        token,
        password,
      })
    );
  }
}
